/*
 * <bnpl-line> — PDP price-row Shop Pay line (2026-09-08).
 *
 * Rendered by snippets/price-bnpl-line.liquid inside #price-{section}. That
 * wrapper's innerHTML is replaced from a server fetch on every variant change
 * (global.js renderProductInfo), so this element is re-created and
 * connectedCallback re-runs with the new variant id — no pubsub needed.
 *
 * Two jobs:
 *  1. In `installments` mode, take the amount / count / eligibility from the
 *     shopify-meta attribute on the sibling <shopify-payment-terms> (server-
 *     rendered by {{ form | payment_terms }}), so the figure is Shop Pay's own.
 *     If the variant isn't eligible for a 0% split-pay plan, fall back to the
 *     static "buy now, pay later" copy rather than show a wrong number.
 *  2. Make the "?" open Shopify's official Installments modal by clicking the
 *     CTA inside shop-js's open shadow root. The host is visually hidden, but
 *     HTMLElement.click() fires regardless of rendering.
 */
if (!customElements.get('bnpl-line')) {
	customElements.define(
		'bnpl-line',
		class BnplLine extends HTMLElement {
			connectedCallback() {
				this.info = this.querySelector('.bnpl-line__info');
				if (this.info) this.info.addEventListener('click', this.openTerms.bind(this));
				this.sync();
			}

			terms() {
				return document.querySelector(
					`#product-form-installment-${this.dataset.section} shopify-payment-terms`
				);
			}

			meta() {
				const el = this.terms();
				if (!el) return null;
				try {
					return JSON.parse(el.getAttribute('shopify-meta'));
				} catch (e) {
					return null;
				}
			}

			num(s) {
				let t = String(s ?? '').replace(/[^0-9.,]/g, '');
				// "36,25" (fr) → 36.25 ; "1,295.00" (en) → 1295.00
				if (t.includes(',') && !t.includes('.')) t = t.replace(',', '.');
				else t = t.replace(/,/g, '');
				return parseFloat(t) || 0;
			}

			format(s, currency, country) {
				const n = this.num(s);
				const lang = (this.dataset.lang || document.documentElement.lang || 'en').slice(0, 2);
				try {
					return new Intl.NumberFormat(`${lang}-${country || 'CA'}`, {
						style: 'currency',
						currency: currency || 'CAD',
						currencyDisplay: 'narrowSymbol',
					}).format(n);
				} catch (e) {
					return String(s);
				}
			}

			/* true when the variant sits in a plan that offers a 0% APR split-pay
			   term matching Shopify's own number_of_payment_terms for it */
			splitPay(meta, v) {
				const n = Number(v.number_of_payment_terms);
				if (!n || n < 2 || n > 4) return false;
				const price = this.num(v.full_price);
				const plan = (meta.financing_plans || []).find(
					(p) => price >= this.num(p.min_price) && price <= this.num(p.max_price)
				);
				if (!plan) return false;
				return (plan.terms || []).some(
					(t) => t.loan_type === 'split_pay' && Number(t.apr) === 0 && Number(t.installments_count) === n
				);
			}

			sync() {
				const copy = this.querySelector('.bnpl-line__copy');
				if (!copy || this.dataset.mode !== 'installments') return;
				const meta = this.meta();
				if (!meta) return; // keep the Liquid paint
				const id = Number(this.dataset.variantId);
				const v = (meta.variants || []).find((x) => Number(x.id) === id);
				if (!v || !v.eligible || !v.available || !this.splitPay(meta, v)) {
					copy.innerHTML = this.dataset.copyBnpl || '';
					this.dataset.resolved = 'bnpl';
					return;
				}
				const amount = this.format(v.price_per_term, meta.currency_code, meta.country_code);
				copy.innerHTML = (this.dataset.copyInstallments || '')
					.replace('%c', String(v.number_of_payment_terms))
					.replace('%a', amount);
				this.dataset.resolved = 'installments';
			}

			openTerms(event) {
				event.preventDefault();
				const tryOpen = () => {
					const el = this.terms();
					const cta = el && el.shadowRoot && el.shadowRoot.querySelector('#shopify-installments-cta');
					if (cta) {
						cta.click();
						return true;
					}
					return false;
				};
				if (tryOpen()) return;
				// shop-js is deferred; on a very early click give it a moment
				let tries = 0;
				const timer = setInterval(() => {
					if (tryOpen() || ++tries > 10) clearInterval(timer);
				}, 150);
			}
		}
	);
}
